import { useTranslation } from '@dagens/frontend-i18n';
import { useNavigate } from 'react-router-dom';
import { Illustrations } from '../../../utils/Illustrations';
import Typography from '@carrot-deprecated/typography';
import Callout from '@carrot-deprecated/callout';
import Theme from '@carrot-deprecated/theme';
import SignupConsumerFields from '_common/components/signup/SignupConsumerFields';
import SignupRequestButton from '_common/components/signup/SignupRequestButton';
import { useAppDispatch, useAppSelector } from '_common/hooks/reduxHooks';
import { useSignupConsumer } from '_common/hooks/useCreateRole';
import usePageView from '_common/hooks/usePageView';
import { loggedIn } from '_common/reducers/auth';
import SignupIntroduction from '_unauthenticated/pages/signup/SignupIntroduction';
import { Page } from '@components/page';
import { createConsumer, getIdentity } from 'api';
import { updateLocalStorage } from 'utils/clientcache';
import {
  CONSUMER_SIGNUP_FAILED,
  CONSUMER_SIGNUP_PAGE_VIEW,
  CONSUMER_SIGNUP_SUBMITTED,
  track
} from 'utils/mixpanel';
import REQ from 'utils/REQ';

const CreateConsumerPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { authenticated, userId, loginEmail } = useAppSelector(({ auth }) => {
    return {
      authenticated: auth.authenticated,
      userId: auth.uid,
      loginEmail: auth.loginEmail
    };
  });

  const [
    req,
    setReq,
    fields,
    onFieldChange,
    validation,
    validate,
    organization,
    onOrganizationChanged
  ] = useSignupConsumer(authenticated);

  usePageView(CONSUMER_SIGNUP_PAGE_VIEW);

  const { t } = useTranslation();

  const onSubmit = async () => {
    if (!validate()) return;

    const { name, contactPerson, deliveryAddress, phone, orgNumber } = fields;

    setReq(REQ.PENDING);
    track(CONSUMER_SIGNUP_SUBMITTED);

    try {
      const { _id } = await createConsumer({
        name,
        contactPerson,
        deliveryAddress,
        phone,
        orgNumber,
        email: loginEmail,
        userId
      });

      setReq(REQ.SUCCESS);

      updateLocalStorage('currentRoleId', _id);
      const userData = await getIdentity({});

      dispatch(loggedIn(userData));

      navigate('/');
    } catch (e) {
      setReq(REQ.ERROR);
      track(CONSUMER_SIGNUP_FAILED);
    }
  };

  const {
    name,
    contactPerson,
    deliveryAddress,
    phone,
    orgNumber,
    acceptTerms
  } = fields;

  const { field: validationErrorField, message: validationErrorMessage } =
    validation;

  return (
    <Page>
      <SignupIntroduction
        illustration={Illustrations.PROUD_PRODUCER_BREAD}
        header={t('signup:consumerPageHeader')}
        introductionText={t('signup:consumerPageParagraph')}
        forOtherUser={t('signup:SignUpForProducers')}
        otherUserQuery="/create-producer"
      />
      <Callout mb="xl">
        <Typography variant="paragraph">
          {t('signup:CreateConsumerCalloutText', {
            loginEmail
          })}
        </Typography>
      </Callout>
      <form
        onSubmit={async e => {
          e.preventDefault();
          await onSubmit();
        }}
        style={{ marginTop: Theme.Spacings.l }}
      >
        <SignupConsumerFields
          name={name}
          contactPerson={contactPerson}
          deliveryAddress={deliveryAddress}
          phone={phone}
          orgNumber={orgNumber}
          acceptTerms={acceptTerms}
          validationErrorField={validationErrorField}
          isLoggedIn={authenticated}
          onChange={onFieldChange}
          organization={organization}
          onOrganizationChanged={onOrganizationChanged}
        />
        <SignupRequestButton
          req={req}
          onSubmit={onSubmit}
          buttonLabel={t('signup:userReadyToBuy')}
          successMessage={t('signup:userCreatedSuccessMessage')}
          validationErrorMessage={validationErrorMessage}
        />
      </form>
    </Page>
  );
};

export default CreateConsumerPage;
